const RELATION_KEY_PREFIX = 'dnaMatch.relationshipValues.';
const missingKeyWarnings = new Set();

const relationAliasMap = {
    'Dochter van achterneef of -nicht': 'dochter_van_achterneef_of_achternicht',
    'In onderzoek: bloedverwant van Josina Akolo': 'in_onderzoek_bloedverwant_van_josina_akolo',
    test: 'onbekend_relatie_label'
};

function normalizePart(part) {
    return String(part || '')
        .trim()
        .replace(/\s+/g, ' ');
}

function slugifyRelationPart(part) {
    const alias = relationAliasMap[part];
    if (alias) {
        return alias;
    }

    return part
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}

function splitRelationParts(value) {
    // Some relations are stored as semicolon-separated composites.
    return String(value || '')
        .split(';')
        .map(normalizePart)
        .filter(Boolean);
}

function joinRelationParts(parts) {
    return parts.join('; ');
}

function isDutchLocale(locale) {
    return String(locale || '').toLowerCase().startsWith('nl');
}

function getText(t, key, fallback) {
    try {
        const value = t(key);
        return typeof value === 'string' ? value : fallback;
    } catch (_error) {
        return fallback;
    }
}

function translateByPattern(part, t) {
    // Pattern fallback keeps translations usable even when a dedicated slug key
    // is not present in locale files.
    let translated = part;

    const replacements = [
        [/In onderzoek:\s*bloedverwant van\s*/gi, getText(t, 'dnaMatch.relationshipTerms.investigationPrefix', 'Under investigation: blood relative of ')],
        [/\s+in de zesde graad/gi, getText(t, 'dnaMatch.relationshipTerms.degreeSix', ' in the sixth degree')],
        [/\s+in de vijfde graad/gi, getText(t, 'dnaMatch.relationshipTerms.degreeFive', ' in the fifth degree')],
        [/\s+in de vierde graad/gi, getText(t, 'dnaMatch.relationshipTerms.degreeFour', ' in the fourth degree')],
        [/\s+in de derde graad/gi, getText(t, 'dnaMatch.relationshipTerms.degreeThree', ' in the third degree')],
        [/\s+van grootmoeder/gi, getText(t, 'dnaMatch.relationshipTerms.fromGrandmother', ' on grandmother\'s side')],
        [/\s+van grootvader/gi, getText(t, 'dnaMatch.relationshipTerms.fromGrandfather', ' on grandfather\'s side')],
        [/\s+van moeder/gi, getText(t, 'dnaMatch.relationshipTerms.fromMother', ' on mother\'s side')],
        [/\s+van vader/gi, getText(t, 'dnaMatch.relationshipTerms.fromFather', ' on father\'s side')],
        [/Kleindochter van\s+/gi, getText(t, 'dnaMatch.relationshipTerms.granddaughterOf', 'Granddaughter of ')],
        [/Kleinzoon van\s+/gi, getText(t, 'dnaMatch.relationshipTerms.grandsonOf', 'Grandson of ')],
        [/Dochter van\s+/gi, getText(t, 'dnaMatch.relationshipTerms.daughterOf', 'Daughter of ')],
        [/Zoon van\s+/gi, getText(t, 'dnaMatch.relationshipTerms.sonOf', 'Son of ')],
        [/Halfgroottante/gi, getText(t, 'dnaMatch.relationshipTerms.halfGreatAunt', 'Half-great-aunt')],
        [/Halfachterneef/gi, getText(t, 'dnaMatch.relationshipTerms.halfSecondCousinMale', 'Half-second cousin (male)')],
        [/Halfachternicht/gi, getText(t, 'dnaMatch.relationshipTerms.halfSecondCousinFemale', 'Half-second cousin (female)')],
        [/Halftante/gi, getText(t, 'dnaMatch.relationshipTerms.halfAunt', 'Half-aunt')],
        [/Achterneef/gi, getText(t, 'dnaMatch.relationshipTerms.secondCousinMale', 'Second cousin (male)')],
        [/Achternicht/gi, getText(t, 'dnaMatch.relationshipTerms.secondCousinFemale', 'Second cousin (female)')],
        [/Halfneef/gi, getText(t, 'dnaMatch.relationshipTerms.halfCousinMale', 'Half-cousin (male)')],
        [/Halfnicht/gi, getText(t, 'dnaMatch.relationshipTerms.halfCousinFemale', 'Half-cousin (female)')],
        [/Zuster/gi, getText(t, 'dnaMatch.relationshipTerms.sister', 'Sister')],
        [/Zus/gi, getText(t, 'dnaMatch.relationshipTerms.sister', 'Sister')],
        [/Broer/gi, getText(t, 'dnaMatch.relationshipTerms.brother', 'Brother')],
        [/Tante/gi, getText(t, 'dnaMatch.relationshipTerms.aunt', 'Aunt')],
        [/Nicht/gi, getText(t, 'dnaMatch.relationshipTerms.cousinFemale', 'Cousin (female)')],
        [/Neef/gi, getText(t, 'dnaMatch.relationshipTerms.cousinMale', 'Cousin (male)')],
        [/Vader/gi, getText(t, 'dnaMatch.relationshipTerms.father', 'Father')],
        [/Moeder/gi, getText(t, 'dnaMatch.relationshipTerms.mother', 'Mother')],
        [/Dochter/gi, getText(t, 'dnaMatch.relationshipTerms.daughter', 'Daughter')],
        [/Zoon/gi, getText(t, 'dnaMatch.relationshipTerms.son', 'Son')],
        [/\s+of\s+-nicht/gi, getText(t, 'dnaMatch.relationshipTerms.orFemaleSuffix', ' or -female cousin')],
    ];

    replacements.forEach(([pattern, replacement]) => {
        translated = translated.replace(pattern, replacement);
    });

    return translated;
}

export function translateDnaRelation(value, t, te, options = {}) {
    const rawSource = value == null ? '' : String(value);
    const source = rawSource.trim();
    if (!source || source === '-') {
        return source || '-';
    }

    // Dutch labels should stay exactly as provided by dnaMatches data.
    if (isDutchLocale(options.locale)) {
        return rawSource;
    }

    const parts = splitRelationParts(source);
    if (!parts.length) {
        return source;
    }

    const translatedParts = parts.map((part) => {
        const slug = slugifyRelationPart(part);
        if (!slug) {
            return part;
        }

        const key = `${RELATION_KEY_PREFIX}${slug}`;

        if (typeof te === 'function' && te(key)) {
            return t(key);
        }

        const patternTranslated = translateByPattern(part, t);
        if (patternTranslated !== part) {
            return patternTranslated;
        }

        if (options.warnOnMissing && typeof console !== 'undefined' && console.warn && !missingKeyWarnings.has(key)) {
            // Warn once per missing key to avoid flooding the console in list views.
            missingKeyWarnings.add(key);
            console.warn(`[dnaRelationI18n] Missing translation for relation key: ${key}`);
        }

        return part;
    });

    return joinRelationParts(translatedParts);
}